import {
    ArrowBack,
    PlayCircleFilledWhiteOutlined,
    ThumbUpRounded,
    YouTube,
} from '@mui/icons-material';
import {
    Avatar,
    CardContent,
    CardHeader,
    Grid,
    IconButton,
    List,
    Modal,
    Stack,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import React, { useState } from 'react';
import {
    Link,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import ShowMoreText from 'react-show-more-text';
import { useGetListDataQuery } from '../../api/apiSlice';
import LightboxModal from '../../components/export/LightboxModal';
import ExternalScreen from '../../components/fragments/ExternalScreen';
import { fbThumbnail } from '../../components/fragments/GuestFeedItem';
import ListEmpty from '../../components/fragments/ListEmpty';
import AudioPlayer from '../../components/utilities/AudioPlayer';
import useSettings from '../../hooks/useSettings';
import { FeedMedia } from '../../tabs/FeedTab';
import { fbEmbed, isYoutube, y2Embed, y2Thmbnail } from '../../utils/embed';

export default function GuestFeedRouteScreen() {
    const [modalOpen, setModalOpen] = useState(false);
    const [openLightbox, setOpenLightbox] = useState(false);
    const [selectedImage, setSelectedImage] = useState(0);

    const { id: feedId } = useParams();
    const { themeMode } = useSettings();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const classes = useFeedStyles();

    const feedType = searchParams.get('type');

    const {
        data: feedData,
        isLoading,
        isFetching,
    } = useGetListDataQuery({
        list: `/guest/${feedType}/${feedId}`,
    });

    const { data: likers } = useGetListDataQuery({
        list: `/${feedType}/${feedId}/likes`,
    });

    const {
        title,
        name,
        description,
        stream,
        audio,
        devotion,
        post,
        media,
        interactions,
        church,
        pastor,
        dates,
        owner,
    } = feedData?.data || {};

    const person = church || pastor;

    const imagesLightbox = media
        ?.filter(({ type }) => type === 'image')
        ?.map(({ link }) => link);

    const goToProfile = () => {
        if (owner == 'CHURCH') {
            navigate(`/church/${person?.id}`);
        } else if (owner === 'PASTOR') {
            navigate(`/minister/${person?.id}`);
        }
    };

    return (
        <ExternalScreen
            title="myKanisa"
            authPass
            disableSpacing
            loading={isLoading || isFetching}
            backAction={
                <IconButton
                    edge="start"
                    className="mr-3"
                    color={themeMode === 'light' ? 'primary' : 'inherit'}
                    sx={{ border: 'none' }}
                    aria-label="menu"
                    onClick={() => navigate('/guest-feed')}
                >
                    <ArrowBack />
                </IconButton>
            }
        >
            <CardHeader
                onClick={goToProfile}
                avatar={<Avatar src={person?.avatar}>R</Avatar>}
                title={person?.name}
                subheader={moment(dates?.created_at).format('MMMM Do YYYY')}
            />

            {!feedType && (
                <ListEmpty
                    title={
                        <>
                            Could not find this post. Kindly go back to the{' '}
                            <Link to="/guest-feed">feed</Link> page.
                        </>
                    }
                />
            )}
            {feedType && (
                <Grid item xs={12}>
                    {media && <FeedMedia media={media} />}
                    {stream && (
                        <div
                            className={classes.stream}
                            style={{
                                backgroundImage: isYoutube(stream)
                                    ? `url(${y2Thmbnail(stream)})`
                                    : `url(${fbThumbnail})`,
                                height: ((window.innerWidth - 64) * 9) / 16,
                            }}
                        >
                            <IconButton onClick={() => setModalOpen(true)}>
                                {isYoutube(stream) ? (
                                    <YouTube
                                        color="secondary"
                                        className={classes.icon}
                                    />
                                ) : (
                                    <PlayCircleFilledWhiteOutlined
                                        sx={{ color: 'white' }}
                                        className={classes.icon}
                                    />
                                )}
                            </IconButton>
                        </div>
                    )}
                    {audio && <AudioPlayer bR audio={audio} />}
                    <CardContent
                        className="py-0"
                        onClick={() => media && setOpenLightbox(true)}
                    >
                        <Typography
                            style={{ textTransform: 'capitalize' }}
                            className="fw-bold mt-2 mb-1"
                        >
                            {(title || name)?.toLowerCase()}
                        </Typography>
                        <ShowMoreText lines={2} more="more" less="less">
                            {description || devotion || post || ' '}
                        </ShowMoreText>
                        <div className="mb-3"></div>

                        <Stack
                            sx={{ mt: 1 }}
                            className="align-items-center justify-content-between"
                            direction="row"
                        >
                            <div className="d-flex align-items-center">
                                <ThumbUpRounded
                                    className="text-white"
                                    sx={{
                                        mr: 0.5,
                                        width: 16,
                                        height: 16,
                                        bgcolor: 'primary.main',
                                        padding: 0.2,
                                        borderRadius: 1,
                                    }}
                                />
                                <Typography variant="body2">
                                    {interactions?.likes}
                                </Typography>
                            </div>

                            <Typography variant="body2">0 Shares</Typography>
                        </Stack>
                    </CardContent>
                    <CardContent>
                        <List>
                            {likers?.data?.map(({ id, member }) => (
                                <Stack direction="row" key={id}>
                                    <LikeAvatar url1={member?.avatar} />
                                    <Typography
                                        sx={{ mt: 1, ml: 2 }}
                                        className="fw-bold"
                                    >
                                        {member?.name}
                                    </Typography>
                                </Stack>
                            ))}
                        </List>
                    </CardContent>
                    <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                        <div className={classes.modal}>
                            <iframe
                                className={classes.iframe}
                                src={
                                    isYoutube(stream)
                                        ? y2Embed(stream)
                                        : fbEmbed(stream)
                                }
                                title={title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </Modal>
                </Grid>
            )}
            {media && (
                <LightboxModal
                    images={imagesLightbox}
                    mainSrc={imagesLightbox[selectedImage]}
                    photoIndex={selectedImage}
                    setPhotoIndex={setSelectedImage}
                    isOpen={openLightbox}
                    onCloseRequest={() => setOpenLightbox(false)}
                />
            )}
        </ExternalScreen>
    );
}

const LikeAvatar = ({ url1 }) => {
    const classes = useFeedStyles();

    return (
        <div>
            <Avatar src={url1 || 'https://ui-avatars.com/api/?name=Guest'} />
            <div className="d-flex justify-content-end">
                <Avatar
                    className={classes.memberAvatar}
                    sx={{ bgcolor: 'primary.main' }}
                >
                    <ThumbUpRounded
                        className="text-white"
                        sx={{
                            width: 16,
                            height: 16,
                            bgcolor: 'primary.main',
                            padding: 0.2,
                            borderRadius: 1,
                        }}
                    />
                </Avatar>
            </div>
        </div>
    );
};

export const useFeedStyles = makeStyles((theme) => ({
    grid: {
        borderTopWidth: 2,
        borderTopColor: theme.palette.divider,
        borderTopStyle: 'solid',
    },
    memberAvatar: {
        width: 22,
        height: 22,
        position: 'relative',
        top: -20,
        border: `1px solid ${theme.palette.background.default}`,
    },
    stream: {
        width: '100%',
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,.3)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'soft-light',
    },
    icon: {
        width: 62,
        height: 62,
    },
    modal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        bgcolor: 'background.paper',
    },
    iframe: {
        width: window.innerWidth,
        height: window.innerWidth * (9 / 16),
        backgroundColor: theme.palette.background.default,
    },
}));
